import type { DataSourceId, DataSourceProvider, DataSourceType } from '../../contexts/DataSourceContext';

export type WelcomeSourceAvailability = 'available' | 'unavailable';

export interface WelcomeSourceDescriptor {
  id: DataSourceId;
  label: string;
  provider: DataSourceProvider;
  type: DataSourceType;
  availability: WelcomeSourceAvailability;
  availabilityReason?: string;
  permissionText: string;
  oauthStartMode: 'google-worker-popup' | 'microsoft-pkce-popup' | 'plaid-link' | 'browser-extension';
  repairRoute: string;
}

export const WELCOME_SOURCE_SKIP_OUTCOME = 'user_skipped_sources' as const;
export const WELCOME_SOURCE_SKIP_REASON = 'user-skipped-sources' as const;
export const WELCOME_SOURCE_SKIP_CONSEQUENCE_COPY =
  'Missions may remain unavailable until you connect a source.';
export const WELCOME_SOURCE_SKIP_NO_GRANT_COPY =
  'No OwnYou token grant is recorded when source setup is skipped.';

export const WELCOME_DAY_ONE_AVAILABLE_SOURCES: readonly DataSourceId[] = [
  'gmail',
  'outlook',
  'google-calendar',
  'microsoft-calendar',
] as const;

export const WELCOME_SOURCE_CATALOG: readonly WelcomeSourceDescriptor[] = [
  {
    id: 'gmail',
    label: 'Gmail',
    provider: 'google',
    type: 'email',
    availability: 'available',
    permissionText: 'Read Gmail metadata and message content for local mission-building analysis.',
    oauthStartMode: 'google-worker-popup',
    repairRoute: '/connections/gmail',
  },
  {
    id: 'outlook',
    label: 'Outlook',
    provider: 'microsoft',
    type: 'email',
    availability: 'available',
    permissionText: 'Read Outlook metadata and message content for local mission-building analysis.',
    oauthStartMode: 'microsoft-pkce-popup',
    repairRoute: '/connections/outlook',
  },
  {
    id: 'google-calendar',
    label: 'Google Calendar',
    provider: 'google',
    type: 'calendar',
    availability: 'available',
    permissionText: 'Read calendar event metadata for schedule-aware missions.',
    oauthStartMode: 'google-worker-popup',
    repairRoute: '/connections/google-calendar',
  },
  {
    id: 'microsoft-calendar',
    label: 'Microsoft Calendar',
    provider: 'microsoft',
    type: 'calendar',
    availability: 'available',
    permissionText: 'Read Microsoft Calendar event metadata for schedule-aware missions.',
    oauthStartMode: 'microsoft-pkce-popup',
    repairRoute: '/connections/microsoft-calendar',
  },
  {
    id: 'plaid',
    label: 'Plaid',
    provider: 'plaid',
    type: 'financial',
    availability: 'unavailable',
    availabilityReason: 'Financial setup is not ready in Slice 08.',
    permissionText: 'Read account and transaction metadata when financial setup is proved.',
    oauthStartMode: 'plaid-link',
    repairRoute: '/connections/plaid',
  },
  {
    id: 'chrome-history',
    label: 'Browser History',
    provider: 'browser-extension',
    type: 'browsing',
    availability: 'unavailable',
    availabilityReason: 'Browser History requires the extension permission path.',
    permissionText: 'Read local browser history after extension permission is granted.',
    oauthStartMode: 'browser-extension',
    repairRoute: '/connections/chrome-history',
  },
] as const;

export function isWelcomeSourceId(value: string): value is DataSourceId {
  return WELCOME_SOURCE_CATALOG.some(source => source.id === value);
}

export function isConcreteWelcomeSourceId(value: string): value is DataSourceId {
  return isWelcomeSourceId(value);
}

export function getWelcomeSourceDescriptor(sourceId: DataSourceId): WelcomeSourceDescriptor {
  const descriptor = WELCOME_SOURCE_CATALOG.find(source => source.id === sourceId);
  if (!descriptor) {
    throw new Error(`Unknown Welcome source: ${sourceId}`);
  }
  return descriptor;
}
